import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { GuardData, updateGuard, uploadImage, generateQRCode, convertToDate } from '../service/guardService';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiUser, FiEdit, FiX } from 'react-icons/fi';
import { FaChevronDown, FaChevronUp, FaDownload } from 'react-icons/fa';

const ModalContent = styled.div`
  max-height: 70vh;
  overflow-y: auto;
`;

const GuardHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[6]};
`;

const GuardName = styled.h3`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[2]};
`;

const CompanyName = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  margin: 0;
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 3px solid ${theme.colors.light.border.medium};
  overflow: hidden;
  margin-bottom: ${theme.spacing[3]};
  background: ${theme.colors.light.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GuardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Section = styled.div`
  margin-bottom: ${theme.spacing[4]};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing[4]};
  background: ${theme.colors.primary[50]};
  cursor: pointer;
  user-select: none;
  transition: background 0.2s ease;

  &:hover {
    background: ${theme.colors.primary[100]};
  }
`;

const SectionTitle = styled.h4`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary[700]};
  margin: 0;
`;

const SectionContent = styled.div<{ isOpen: boolean }>`
  padding: ${theme.spacing[4]};
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing[4]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.primary};
  cursor: pointer;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.primary};
  cursor: pointer;
`;

const Divider = styled.div`
  height: 1px;
  background: ${theme.colors.light.border.light};
  margin: ${theme.spacing[4]} 0;
`;

const QRCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spacing[4]};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.lg};
  margin-top: ${theme.spacing[4]};
`;

const QRCodeImage = styled.img`
  max-width: 200px;
  height: auto;
  margin-bottom: ${theme.spacing[3]};
`;

interface GuardModalProps {
  guard: GuardData;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  canEdit?: boolean;
}

const GuardModal: React.FC<GuardModalProps> = ({ guard, isOpen, onClose, onUpdate, canEdit = true }) => {
  const { companyData, user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editedGuard, setEditedGuard] = useState<GuardData>(guard);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['personalInfo']));
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [generatingQR, setGeneratingQR] = useState(false);

  // Get company ID - prioritize companyData.id, fallback to hardcoded ID if needed
  const companyId = companyData?.id || user?.uid || '';

  useEffect(() => {
    setEditedGuard(guard);
  }, [guard]);

  useEffect(() => {
    if (guard?.link && isOpen) {
      generateQR();
    }
  }, [guard, isOpen]);

  const generateQR = async () => {
    if (!guard.link) return;
    setGeneratingQR(true);
    try {
      const fullUrl = `https://guarding.dynamixzone.com${guard.link}`;
      const qrDataUrl = await generateQRCode(fullUrl);
      setQrCodeDataUrl(qrDataUrl);
    } catch (error) {
      console.error('QR generation error:', error);
    } finally {
      setGeneratingQR(false);
    }
  };

  const toggleSection = (section: string) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const handleFieldChange = (field: string, value: any, section?: string, index?: number) => {
    setEditedGuard((prev) => {
      if (!section) {
        return { ...prev, [field]: value };
      } else if (section === 'emergencyContact') {
        return {
          ...prev,
          emergencyContact: { ...prev.emergencyContact, [field]: value },
        };
      } else if (section === 'services') {
        return {
          ...prev,
          services: { ...prev.services, [field]: value },
        };
      } else if (section === 'questions') {
        return {
          ...prev,
          questions: { ...prev.questions, [field]: value },
        };
      } else if (section === 'references' && index !== undefined) {
        const newReferences = [...(prev.references || [])];
        newReferences[index] = { ...newReferences[index], [field]: value };
        return { ...prev, references: newReferences };
      }
      return prev;
    });
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const imageUrl = await uploadImage(file);
      handleFieldChange('image', imageUrl);
      toast.success('Image uploaded successfully');
    } catch (error: any) {
      console.error('Image upload error:', error);
      toast.error('Image upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleUpdate = async () => {
    if (!editedGuard.id) {
      toast.error('Invalid guard selected');
      return;
    }

    try {
      await updateGuard(editedGuard.id, editedGuard, companyId);
      toast.success('Guard updated successfully');
      setIsEditing(false);
      onUpdate();
    } catch (error: any) {
      console.error('Update failed:', error);
      toast.error('Failed to update guard');
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeDataUrl) return;
    const link = document.createElement('a');
    link.download = `guard-qr-${guard?.guardId || 'code'}.png`;
    link.href = qrCodeDataUrl;
    link.click();
  };

  const formatCNIC = (v: string) => {
    const digits = v.replace(/\D/g, '').slice(0, 13);
    if (digits.length <= 5) return digits;
    if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5, 12)}`;
    return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;
  };

  const formatMobile = (v: string) => {
    const digits = v.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 4) return digits;
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Guard Information"
      footer={
        <>
          {!isEditing ? (
            <>
              {canEdit && (
                <Button onClick={() => setIsEditing(true)}>
                  <FiEdit size={18} />
                  Edit
                </Button>
              )}
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setIsEditing(false)} disabled={uploadingImage}>
                Cancel
              </Button>
              <Button onClick={handleUpdate} disabled={uploadingImage} loading={uploadingImage}>
                Update
              </Button>
            </>
          )}
        </>
      }
    >
      <ModalContent>
        <GuardHeader>
          <GuardName>{guard.name || '—'}</GuardName>
          <CompanyName>{guard.companyName || '—'}</CompanyName>
        </GuardHeader>

        <ImageContainer>
          <ImageWrapper>
            {guard.image ? (
              <GuardImage src={guard.image} alt={guard.name} />
            ) : (
              <FiUser size={48} style={{ color: theme.colors.light.text.muted }} />
            )}
          </ImageWrapper>
          {isEditing && (
            <label>
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                disabled={uploadingImage}
                style={{ display: 'none' }}
              />
              <span>
                <Button size="sm" variant="outline" disabled={uploadingImage} onClick={() => document.getElementById('image-upload')?.click()}>
                  {uploadingImage ? 'Uploading...' : 'Change Image'}
                </Button>
              </span>
            </label>
          )}
        </ImageContainer>

        {/* Personal Info Section */}
        <Section>
          <SectionHeader onClick={() => toggleSection('personalInfo')}>
            <SectionTitle>Personal Information</SectionTitle>
            {openSections.has('personalInfo') ? <FaChevronUp /> : <FaChevronDown />}
          </SectionHeader>
          <SectionContent isOpen={openSections.has('personalInfo')}>
            <FormGroup>
              <Input
                label="Guard ID"
                value={editedGuard.guardId || ''}
                disabled
                dark
                fullWidth
              />
            </FormGroup>
            <FormRow>
              <FormGroup>
                <Input
                  label="Name"
                  value={editedGuard.name || ''}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  disabled={!isEditing}
                  dark
                  fullWidth
                />
              </FormGroup>
              <FormGroup>
                <Input
                  label="Father Name"
                  value={editedGuard.fatherName || ''}
                  onChange={(e) => handleFieldChange('fatherName', e.target.value)}
                  disabled={!isEditing}
                  dark
                  fullWidth
                />
              </FormGroup>
            </FormRow>
            <FormGroup>
              <Input
                label="CNIC"
                value={editedGuard.cnic || ''}
                onChange={(e) => handleFieldChange('cnic', formatCNIC(e.target.value))}
                disabled={!isEditing}
                dark
                fullWidth
              />
            </FormGroup>
            <FormRow>
              <FormGroup>
                <Input
                  label="Contact No 1"
                  value={editedGuard.contactNo1 || ''}
                  onChange={(e) => handleFieldChange('contactNo1', formatMobile(e.target.value))}
                  disabled={!isEditing}
                  dark
                  fullWidth
                />
              </FormGroup>
              <FormGroup>
                <Input
                  label="Contact No 2"
                  value={editedGuard.contactNo2 || ''}
                  onChange={(e) => handleFieldChange('contactNo2', formatMobile(e.target.value))}
                  disabled={!isEditing}
                  dark
                  fullWidth
                />
              </FormGroup>
            </FormRow>
            <FormGroup>
              <Input
                label="Salary"
                value={editedGuard.salary ? String(editedGuard.salary) : ''}
                onChange={(e) => handleFieldChange('salary', e.target.value)}
                disabled={!isEditing}
                dark
                fullWidth
                placeholder="Enter salary amount"
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Date of Birth"
                type="date"
                value={
                  editedGuard.dateOfBirth
                    ? convertToDate(editedGuard.dateOfBirth)?.toISOString().split('T')[0] || ''
                    : ''
                }
                onChange={(e) => {
                  if (isEditing) {
                    const date = e.target.value ? new Date(e.target.value) : null;
                    handleFieldChange('dateOfBirth', date);
                  }
                }}
                disabled={!isEditing}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Education"
                value={editedGuard.education || ''}
                onChange={(e) => handleFieldChange('education', e.target.value)}
                disabled={!isEditing}
                dark
                fullWidth
              />
            </FormGroup>
            <FormRow>
              <FormGroup>
                <Input
                  label="Date of Joining"
                  type="date"
                  value={
                    editedGuard.dateOfJoining
                      ? convertToDate(editedGuard.dateOfJoining)?.toISOString().split('T')[0] || ''
                      : ''
                  }
                  onChange={(e) => {
                    if (isEditing) {
                      const date = e.target.value ? new Date(e.target.value) : null;
                      handleFieldChange('dateOfJoining', date);
                    }
                  }}
                  disabled={!isEditing}
                  dark
                  fullWidth
                />
              </FormGroup>
              <FormGroup>
                <Input
                  label="Date of Registration"
                  type="date"
                  value={
                    editedGuard.dateOfRegistration
                      ? convertToDate(editedGuard.dateOfRegistration)?.toISOString().split('T')[0] || ''
                      : ''
                  }
                  onChange={(e) => {
                    if (isEditing) {
                      const date = e.target.value ? new Date(e.target.value) : null;
                      handleFieldChange('dateOfRegistration', date);
                    }
                  }}
                  disabled={!isEditing}
                  dark
                  fullWidth
                />
              </FormGroup>
            </FormRow>
            <FormGroup>
              <Input
                label="CNIC Expiry"
                type="date"
                value={
                  editedGuard.cnicExpiry
                    ? convertToDate(editedGuard.cnicExpiry)?.toISOString().split('T')[0] || ''
                    : ''
                }
                onChange={(e) => {
                  if (isEditing) {
                    const date = e.target.value ? new Date(e.target.value) : null;
                    handleFieldChange('cnicExpiry', date);
                  }
                }}
                disabled={!isEditing}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Language"
                value={editedGuard.language || ''}
                onChange={(e) => handleFieldChange('language', e.target.value)}
                disabled={!isEditing}
                dark
                fullWidth
              />
            </FormGroup>
            <CheckboxGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={editedGuard.married || false}
                  onChange={(e) => handleFieldChange('married', e.target.checked)}
                  disabled={!isEditing}
                />
                Married
              </CheckboxLabel>
            </CheckboxGroup>
          </SectionContent>
        </Section>

        {/* Address Section */}
        <Section>
          <SectionHeader onClick={() => toggleSection('address')}>
            <SectionTitle>Address Information</SectionTitle>
            {openSections.has('address') ? <FaChevronUp /> : <FaChevronDown />}
          </SectionHeader>
          <SectionContent isOpen={openSections.has('address')}>
            <FormGroup>
              <Input
                label="Current Address"
                value={editedGuard.currentAddress || ''}
                onChange={(e) => handleFieldChange('currentAddress', e.target.value)}
                disabled={!isEditing}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Permanent Address"
                value={editedGuard.permanentAddress || ''}
                onChange={(e) => handleFieldChange('permanentAddress', e.target.value)}
                disabled={!isEditing || editedGuard.sameAddress}
                dark
                fullWidth
              />
            </FormGroup>
            <FormRow>
              <FormGroup>
                <Input
                  label="Police District Current"
                  value={editedGuard.policeDistrictCurrent || ''}
                  onChange={(e) => handleFieldChange('policeDistrictCurrent', e.target.value)}
                  disabled={!isEditing}
                  dark
                  fullWidth
                />
              </FormGroup>
              <FormGroup>
                <Input
                  label="Police District Permanent"
                  value={editedGuard.policeDistrictPermanent || ''}
                  onChange={(e) => handleFieldChange('policeDistrictPermanent', e.target.value)}
                  disabled={!isEditing || editedGuard.sameAddress}
                  dark
                  fullWidth
                />
              </FormGroup>
            </FormRow>
            <CheckboxGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={editedGuard.sameAddress || false}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    handleFieldChange('sameAddress', checked);
                    if (checked && isEditing) {
                      handleFieldChange('permanentAddress', editedGuard.currentAddress || '');
                      handleFieldChange('policeDistrictPermanent', editedGuard.policeDistrictCurrent || '');
                    }
                  }}
                  disabled={!isEditing}
                />
                Current and Permanent Address are Same
              </CheckboxLabel>
            </CheckboxGroup>
          </SectionContent>
        </Section>

        {/* Emergency Contact Section */}
        <Section>
          <SectionHeader onClick={() => toggleSection('emergencyContact')}>
            <SectionTitle>Emergency Contact</SectionTitle>
            {openSections.has('emergencyContact') ? <FaChevronUp /> : <FaChevronDown />}
          </SectionHeader>
          <SectionContent isOpen={openSections.has('emergencyContact')}>
            <FormRow>
              <FormGroup>
                <Input
                  label="Name"
                  value={editedGuard.emergencyContact?.name || ''}
                  onChange={(e) => handleFieldChange('name', e.target.value, 'emergencyContact')}
                  disabled={!isEditing}
                  dark
                  fullWidth
                />
              </FormGroup>
              <FormGroup>
                <Input
                  label="Contact No"
                  value={editedGuard.emergencyContact?.contactNo || ''}
                  onChange={(e) =>
                    handleFieldChange('contactNo', formatMobile(e.target.value), 'emergencyContact')
                  }
                  disabled={!isEditing}
                  dark
                  fullWidth
                />
              </FormGroup>
            </FormRow>
            <FormGroup>
              <Input
                label="Address"
                value={editedGuard.emergencyContact?.address || ''}
                onChange={(e) => handleFieldChange('address', e.target.value, 'emergencyContact')}
                disabled={!isEditing}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="CNIC"
                value={editedGuard.emergencyContact?.cnic || ''}
                onChange={(e) => handleFieldChange('cnic', formatCNIC(e.target.value), 'emergencyContact')}
                disabled={!isEditing}
                dark
                fullWidth
              />
            </FormGroup>
          </SectionContent>
        </Section>

        {/* Services Section */}
        <Section>
          <SectionHeader onClick={() => toggleSection('services')}>
            <SectionTitle>Services</SectionTitle>
            {openSections.has('services') ? <FaChevronUp /> : <FaChevronDown />}
          </SectionHeader>
          <SectionContent isOpen={openSections.has('services')}>
            <FormGroup>
              <label style={{ 
                display: 'block', 
                fontSize: theme.typography.fontSize.sm, 
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.light.text.primary,
                marginBottom: theme.spacing[2]
              }}>
                Service Type
              </label>
              <RadioGroup>
                <RadioLabel>
                  <input
                    type="radio"
                    name="serviceType"
                    checked={editedGuard.services?.type === 'civilian'}
                    onChange={() => handleFieldChange('type', 'civilian', 'services')}
                    disabled={!isEditing}
                  />
                  Civilian
                </RadioLabel>
                <RadioLabel>
                  <input
                    type="radio"
                    name="serviceType"
                    checked={editedGuard.services?.type === 'serviceRetired'}
                    onChange={() => handleFieldChange('type', 'serviceRetired', 'services')}
                    disabled={!isEditing}
                  />
                  Service Retired
                </RadioLabel>
              </RadioGroup>
            </FormGroup>
            {editedGuard.services?.type === 'serviceRetired' && (
              <>
                <FormGroup>
                  <Input
                    label="Unit No"
                    value={editedGuard.services?.unitNo || ''}
                    onChange={(e) => handleFieldChange('unitNo', e.target.value, 'services')}
                    disabled={!isEditing}
                    dark
                    fullWidth
                  />
                </FormGroup>
                <FormGroup>
                  <Input
                    label="Experience Years"
                    value={editedGuard.services?.experienceYears || ''}
                    onChange={(e) => handleFieldChange('experienceYears', e.target.value, 'services')}
                    disabled={!isEditing}
                    dark
                    fullWidth
                  />
                </FormGroup>
              </>
            )}
          </SectionContent>
        </Section>

        {/* Questions Section */}
        <Section>
          <SectionHeader onClick={() => toggleSection('questions')}>
            <SectionTitle>Questions</SectionTitle>
            {openSections.has('questions') ? <FaChevronUp /> : <FaChevronDown />}
          </SectionHeader>
          <SectionContent isOpen={openSections.has('questions')}>
            <CheckboxGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={editedGuard.questions?.workedAsGuard || false}
                  onChange={(e) => handleFieldChange('workedAsGuard', e.target.checked, 'questions')}
                  disabled={!isEditing}
                />
                Worked as Security Guard in Any Company
              </CheckboxLabel>
            </CheckboxGroup>
            {editedGuard.questions?.workedAsGuard && (
              <>
                <FormGroup>
                  <Input
                    label="Company Name"
                    value={editedGuard.questions?.companyName || ''}
                    onChange={(e) => handleFieldChange('companyName', e.target.value, 'questions')}
                    disabled={!isEditing}
                    dark
                    fullWidth
                  />
                </FormGroup>
                <FormGroup>
                  <Input
                    label="Reason of Leaving"
                    value={editedGuard.questions?.reasonOfLeaving || ''}
                    onChange={(e) => handleFieldChange('reasonOfLeaving', e.target.value, 'questions')}
                    disabled={!isEditing}
                    dark
                    fullWidth
                  />
                </FormGroup>
              </>
            )}
            <FormGroup>
              <label style={{ 
                display: 'block', 
                fontSize: theme.typography.fontSize.sm, 
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.light.text.primary,
                marginBottom: theme.spacing[2]
              }}>
                APSSA Trained
              </label>
              <RadioGroup>
                <RadioLabel>
                  <input
                    type="radio"
                    name="apssaTrained"
                    checked={editedGuard.questions?.apssaTrained === true}
                    onChange={() => handleFieldChange('apssaTrained', true, 'questions')}
                    disabled={!isEditing}
                  />
                  Yes
                </RadioLabel>
                <RadioLabel>
                  <input
                    type="radio"
                    name="apssaTrained"
                    checked={editedGuard.questions?.apssaTrained === false}
                    onChange={() => handleFieldChange('apssaTrained', false, 'questions')}
                    disabled={!isEditing}
                  />
                  No
                </RadioLabel>
              </RadioGroup>
            </FormGroup>
            <FormGroup>
              <label style={{ 
                display: 'block', 
                fontSize: theme.typography.fontSize.sm, 
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.light.text.primary,
                marginBottom: theme.spacing[2]
              }}>
                Work Anywhere
              </label>
              <RadioGroup>
                <RadioLabel>
                  <input
                    type="radio"
                    name="workAnywhere"
                    checked={editedGuard.questions?.workAnywhere === true}
                    onChange={() => handleFieldChange('workAnywhere', true, 'questions')}
                    disabled={!isEditing}
                  />
                  Yes
                </RadioLabel>
                <RadioLabel>
                  <input
                    type="radio"
                    name="workAnywhere"
                    checked={editedGuard.questions?.workAnywhere === false}
                    onChange={() => handleFieldChange('workAnywhere', false, 'questions')}
                    disabled={!isEditing}
                  />
                  No
                </RadioLabel>
              </RadioGroup>
            </FormGroup>
            {editedGuard.services?.type === 'serviceRetired' && (
              <FormGroup>
                <label style={{ 
                  display: 'block', 
                  fontSize: theme.typography.fontSize.sm, 
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2]
                }}>
                  Army Court Case
                </label>
                <RadioGroup>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="armyCourtCase"
                      checked={editedGuard.questions?.armyCourtCase === true}
                      onChange={() => handleFieldChange('armyCourtCase', true, 'questions')}
                      disabled={!isEditing}
                    />
                    Yes
                  </RadioLabel>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="armyCourtCase"
                      checked={editedGuard.questions?.armyCourtCase === false}
                      onChange={() => handleFieldChange('armyCourtCase', false, 'questions')}
                      disabled={!isEditing}
                    />
                    No
                  </RadioLabel>
                </RadioGroup>
              </FormGroup>
            )}
          </SectionContent>
        </Section>

        {/* References Section */}
        <Section>
          <SectionHeader onClick={() => toggleSection('references')}>
            <SectionTitle>References</SectionTitle>
            {openSections.has('references') ? <FaChevronUp /> : <FaChevronDown />}
          </SectionHeader>
          <SectionContent isOpen={openSections.has('references')}>
            {((editedGuard.references && editedGuard.references.length > 0) ? editedGuard.references : [
              { name: '', address: '', cnic: '', contact: '', relation: '' },
              { name: '', address: '', cnic: '', contact: '', relation: '' }
            ]).map((ref: any, index: number) => (
              <div key={index}>
                <h6 style={{ 
                  fontSize: theme.typography.fontSize.base, 
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[3]
                }}>
                  Person {index + 1}
                </h6>
                <FormRow>
                  <FormGroup>
                    <Input
                      label="Name"
                      value={ref.name || ''}
                      onChange={(e) => handleFieldChange('name', e.target.value, 'references', index)}
                      disabled={!isEditing}
                      dark
                      fullWidth
                    />
                  </FormGroup>
                  <FormGroup>
                    <Input
                      label="Contact"
                      value={ref.contact || ''}
                      onChange={(e) => handleFieldChange('contact', formatMobile(e.target.value), 'references', index)}
                      disabled={!isEditing}
                      dark
                      fullWidth
                      placeholder="03xx-xxxxxxx"
                    />
                  </FormGroup>
                </FormRow>
                <FormGroup>
                  <Input
                    label="Address"
                    value={ref.address || ''}
                    onChange={(e) => handleFieldChange('address', e.target.value, 'references', index)}
                    disabled={!isEditing}
                    dark
                    fullWidth
                  />
                </FormGroup>
                <FormRow>
                  <FormGroup>
                    <Input
                      label="CNIC"
                      value={ref.cnic || ''}
                      onChange={(e) => handleFieldChange('cnic', formatCNIC(e.target.value), 'references', index)}
                      disabled={!isEditing}
                      dark
                      fullWidth
                      placeholder="xxxxx-xxxxxxx-x"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Input
                      label="Relation"
                      value={ref.relation || ''}
                      onChange={(e) => handleFieldChange('relation', e.target.value, 'references', index)}
                      disabled={!isEditing}
                      dark
                      fullWidth
                    />
                  </FormGroup>
                </FormRow>
                {index === 0 && <Divider />}
              </div>
            ))}
          </SectionContent>
        </Section>

        {/* QR Code Section */}
        {guard.link && (
          <QRCodeContainer>
            <h5 style={{ marginBottom: theme.spacing[3] }}>QR Code</h5>
            {generatingQR ? (
              <div>Generating QR Code...</div>
            ) : qrCodeDataUrl ? (
              <>
                <QRCodeImage src={qrCodeDataUrl} alt="QR Code" />
                <Button size="sm" variant="outline" onClick={downloadQRCode}>
                  <FaDownload size={18} />
                  Download QR Code
                </Button>
              </>
            ) : (
              <p style={{ color: theme.colors.light.text.muted }}>No QR code available</p>
            )}
          </QRCodeContainer>
        )}
      </ModalContent>
    </Modal>
  );
};

export default GuardModal;

